import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

function formatNumber(num) {
    if (num == null) return '0.00';
    return Number(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function formatLocalDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getDefaultDates(periodType) {
    const now = new Date();
    let startDate, endDate;

    if (periodType === 'daily') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatLocalDate(yesterday);
        endDate = startDate;
    } else if (periodType === 'weekly') {
        const dayOfWeek = now.getDay() || 7;
        const lastSunday = new Date(now);
        lastSunday.setDate(now.getDate() - dayOfWeek);
        const lastMonday = new Date(lastSunday);
        lastMonday.setDate(lastSunday.getDate() - 6);
        startDate = formatLocalDate(lastMonday);
        endDate = formatLocalDate(lastSunday);
    } else if (periodType === 'monthly') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = formatLocalDate(lastMonth);
        endDate = formatLocalDate(lastMonthEnd);
    } else {
        startDate = formatLocalDate(now);
        endDate = startDate;
    }

    return { startDate, endDate };
}

@observer
class PowerofficeVoucherReporting extends Component {
    constructor(props) {
        super(props);

        const { stores } = props;
        const { userStore } = stores;
        const customer = userStore.customer || {};
        const periodType = customer.featureFlags?.powerOfficeVoucherPeriod || 'monthly';
        const { startDate, endDate } = getDefaultDates(periodType);

        this.state = {
            periodType,
            startDate,
            endDate,
            excludeInvoice: true,
            postError: null,
            postSuccess: null,
        };
    }

    componentDidMount() {
        const { stores } = this.props;
        const { powerofficeStore } = stores;
        powerofficeStore.getVoucherHistory();
    }

    componentWillUnmount() {
        const { stores } = this.props;
        const { powerofficeStore } = stores;
        powerofficeStore.clearVoucherData();
    }

    handlePeriodTypeChange = (e) => {
        const periodType = e.target.value;
        const { startDate, endDate } = getDefaultDates(periodType);
        this.setState({ periodType, startDate, endDate, postError: null, postSuccess: null });
    }

    handleDateChange = (e) => {
        this.setState({ [e.target.name]: e.target.value, postError: null, postSuccess: null });
    }

    syncFiltersToSaleStore = async () => {
        const { stores, callback = () => {}, setQuickDateFilter } = this.props;
        const { saleStore } = stores;
        const { startDate, endDate } = this.state;

        saleStore.updateKeyValue('cutoffDate', startDate);
        saleStore.updateKeyValue('cutoffDateEnd', endDate);
        saleStore.updateKeyValue('limit', 1000);

        if (setQuickDateFilter) {
            const d = new Date(startDate);
            setQuickDateFilter(d.getFullYear(), d.getMonth() + 1);
        }

        await callback();
    }

    loadPreview = async () => {
        const { stores } = this.props;
        const { powerofficeStore } = stores;
        const { startDate, endDate, excludeInvoice } = this.state;

        this.setState({ postError: null, postSuccess: null });

        await this.syncFiltersToSaleStore();

        const response = await powerofficeStore.getVoucherPreview({ startDate, endDate, excludeInvoice });
        if (response.status !== 200) {
            this.setState({ postError: response.message || 'Failed to load preview.' });
        }
    }

    postVoucher = async () => {
        const { stores } = this.props;
        const { powerofficeStore } = stores;
        const { startDate, endDate, periodType, excludeInvoice } = this.state;

        this.setState({ postError: null, postSuccess: null });

        const response = await powerofficeStore.postVoucher({ startDate, endDate, periodType, excludeInvoice });
        if (response.status === 201) {
            const voucherNo = response.data?.voucherNumber || response.data?.voucherId || '';
            const msg = voucherNo
                ? `${response.message} Voucher no: ${voucherNo}`
                : response.message;
            this.setState({ postSuccess: msg });
        } else {
            this.setState({ postError: response.message || 'Failed to post voucher.' });
        }
    }

    render() {
        const { stores, onClose } = this.props;
        const { powerofficeStore } = stores;
        const { periodType, startDate, endDate, excludeInvoice, postError, postSuccess } = this.state;

        const preview = powerofficeStore.voucherPreview;
        const previewLoading = powerofficeStore.voucherPreviewLoading;
        const postLoading = powerofficeStore.voucherPostLoading;
        const history = powerofficeStore.voucherHistory || [];
        const historyLoading = powerofficeStore.voucherHistoryLoading;

        return (
            <div class='w-100'>
                {/* Header with close button */}
                <div class='d-flex justify-content-between align-items-center mb-3'>
                    <h5 class='mb-0'>
                        <i class='fa-duotone fa-file-invoice me-2' />
                        <Text id='poweroffice.voucherReporting'>PowerOffice Voucher Reporting</Text>
                    </h5>
                    {onClose && (
                        <button class='btn btn-sm btn-outline-secondary' onClick={onClose}>
                            <i class='fa-solid fa-times me-1' />
                            <Text id='general.close'>Close</Text>
                        </button>
                    )}
                </div>

                {/* Period Selection */}
                <div class='card mb-3'>
                    <div class='card-body'>
                        <div class='row g-3 align-items-end'>
                            <div class='col-auto'>
                                <label class='form-label'><Text id='poweroffice.periodType'>Period Type</Text></label>
                                <select class='form-select' value={periodType} onChange={this.handlePeriodTypeChange}>
                                    <option value='daily'><Text id='poweroffice.periodDaily'>Daily (Dagsoppgjor)</Text></option>
                                    <option value='weekly'><Text id='poweroffice.periodWeekly'>Weekly (Ukesoppgjor)</Text></option>
                                    <option value='monthly'><Text id='poweroffice.periodMonthly'>Monthly (Manedsoppgjor)</Text></option>
                                </select>
                            </div>
                            <div class='col-auto'>
                                <label class='form-label'><Text id='poweroffice.startDate'>Start Date</Text></label>
                                <input type='date' class='form-control' name='startDate' value={startDate} onChange={this.handleDateChange} />
                            </div>
                            <div class='col-auto'>
                                <label class='form-label'><Text id='poweroffice.endDate'>End Date</Text></label>
                                <input type='date' class='form-control' name='endDate' value={endDate} onChange={this.handleDateChange} />
                            </div>
                            <div class='col-auto d-flex align-items-end'>
                                <div class='form-check mb-2'>
                                    <input
                                        class='form-check-input'
                                        type='checkbox'
                                        id='poExcludeInvoice'
                                        checked={excludeInvoice}
                                        onChange={(e) => this.setState({ excludeInvoice: e.target.checked })}
                                    />
                                    <label class='form-check-label' for='poExcludeInvoice'>
                                        <Text id='poweroffice.excludeInvoice'>Exclude invoice sales</Text>
                                    </label>
                                </div>
                            </div>
                            <div class='col-auto'>
                                <button
                                    class='btn btn-primary'
                                    onClick={this.loadPreview}
                                    disabled={previewLoading}
                                >
                                    {previewLoading ? (
                                        <span><i class='fa-solid fa-spinner fa-spin me-1' /> <Text id='poweroffice.loading'>Loading...</Text></span>
                                    ) : (
                                        <span><i class='fa-solid fa-eye me-1' /> <Text id='poweroffice.loadPreview'>Load Preview</Text></span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error/Success Messages */}
                {postError && (
                    <div class='alert alert-danger'>{postError}</div>
                )}
                {postSuccess && (
                    <div class='alert alert-success'>{postSuccess}</div>
                )}

                {/* Preview */}
                {preview && (
                    <div class='card mb-3'>
                        <div class='card-header'>
                            <strong>
                                <Text id='poweroffice.preview'>Preview</Text>
                                : {periodType === 'daily' ? `Dagsoppgjor ${startDate}` :
                                    periodType === 'weekly' ? `Ukesoppgjor ${startDate} - ${endDate}` :
                                    `Manedsoppgjor ${startDate.substring(0, 7)}`}
                            </strong>
                        </div>
                        <div class='card-body'>
                            {/* Sale Counts */}
                            <div class='row mb-3'>
                                <div class='col'>
                                    <span class='badge bg-success me-2'>{preview.salesIncluded}</span>
                                    <Text id='poweroffice.salesIncluded'>Sales included</Text>
                                </div>
                                {preview.excludedByAlreadyTransferred > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-secondary me-2'>{preview.excludedByAlreadyTransferred}</span>
                                        <Text id='poweroffice.excludedByTransferred'>Excluded (already transferred)</Text>
                                    </div>
                                )}
                                {preview.excludedByVoucher > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-secondary me-2'>{preview.excludedByVoucher}</span>
                                        <Text id='poweroffice.excludedByVoucher'>Excluded (already in voucher)</Text>
                                    </div>
                                )}
                                {preview.excludedByInvoiceFilter > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-warning text-dark me-2'>{preview.excludedByInvoiceFilter}</span>
                                        <Text id='poweroffice.excludedByInvoiceFilter'>Excluded (invoice sales)</Text>
                                    </div>
                                )}
                            </div>

                            {/* Revenue Lines */}
                            <h6><Text id='poweroffice.revenue'>Revenue (Credit)</Text></h6>
                            <table class='table table-sm table-striped mb-3'>
                                <thead>
                                    <tr>
                                        <th><Text id='poweroffice.account'>Account</Text></th>
                                        <th><Text id='poweroffice.description'>Description</Text></th>
                                        <th><Text id='poweroffice.vat'>VAT</Text></th>
                                        <th class='text-end'><Text id='poweroffice.amount'>Amount</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.revenueLines || []).map((line, idx) => (
                                        <tr key={`rev-${idx}`}>
                                            <td>{line.account}</td>
                                            <td>{line.description}</td>
                                            <td>{line.vatRate}%</td>
                                            <td class='text-end'>{formatNumber(line.amountGross)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Payment Lines */}
                            <h6><Text id='poweroffice.payments'>Payments (Debit)</Text></h6>
                            <table class='table table-sm table-striped mb-3'>
                                <thead>
                                    <tr>
                                        <th><Text id='poweroffice.account'>Account</Text></th>
                                        <th><Text id='poweroffice.description'>Description</Text></th>
                                        <th class='text-end'><Text id='poweroffice.amount'>Amount</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.paymentLines || []).map((line, idx) => (
                                        <tr key={`pay-${idx}`}>
                                            <td>{line.account}</td>
                                            <td>{line.description}</td>
                                            <td class='text-end'>{formatNumber(line.amountGross)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Summary */}
                            <h6><Text id='poweroffice.summary'>Summary</Text></h6>
                            <table class='table table-sm'>
                                <tbody>
                                    <tr>
                                        <td><Text id='poweroffice.totalRevenueExVat'>Total revenue (ex. VAT)</Text></td>
                                        <td class='text-end'>{formatNumber(preview.summary?.totalRevenueExVat)}</td>
                                    </tr>
                                    <tr>
                                        <td><Text id='poweroffice.totalVat'>Total VAT</Text></td>
                                        <td class='text-end'>{formatNumber(preview.summary?.totalVat)}</td>
                                    </tr>
                                    <tr>
                                        <td><strong><Text id='poweroffice.totalGross'>Total gross</Text></strong></td>
                                        <td class='text-end'><strong>{formatNumber(preview.summary?.totalGross)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td><Text id='poweroffice.totalPayments'>Total payments</Text></td>
                                        <td class='text-end'>{formatNumber(preview.summary?.totalPayments)}</td>
                                    </tr>
                                    {preview.summary?.imbalance !== 0 && (
                                        <tr>
                                            <td>
                                                <Text id='poweroffice.imbalance'>Imbalance</Text>
                                                {preview.summary?.balanced ? (
                                                    <span class='badge bg-warning ms-2'><Text id='poweroffice.rounding'>Rounding</Text></span>
                                                ) : (
                                                    <span class='badge bg-danger ms-2'><Text id='poweroffice.notBalanced'>Not balanced</Text></span>
                                                )}
                                            </td>
                                            <td class='text-end'>{formatNumber(preview.summary?.imbalance)}</td>
                                        </tr>
                                    )}
                                    <tr class={preview.summary?.balanced ? 'table-success' : 'table-danger'}>
                                        <td>
                                            <strong><Text id='poweroffice.balance'>Balance</Text></strong>
                                            {preview.summary?.balanced ? (
                                                <i class='fa-solid fa-check-circle text-success ms-2' />
                                            ) : (
                                                <i class='fa-solid fa-times-circle text-danger ms-2' />
                                            )}
                                        </td>
                                        <td class='text-end'>
                                            <strong>{preview.summary?.balanced ? '0.00' : formatNumber(preview.summary?.imbalance)}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Post Button */}
                            <div class='d-flex justify-content-end mt-3'>
                                <button
                                    class='btn btn-success btn-lg'
                                    onClick={this.postVoucher}
                                    disabled={!preview.summary?.balanced || postLoading || preview.salesIncluded === 0}
                                >
                                    {postLoading ? (
                                        <span><i class='fa-solid fa-spinner fa-spin me-1' /> <Text id='poweroffice.posting'>Posting...</Text></span>
                                    ) : (
                                        <span><i class='fa-solid fa-paper-plane me-1' /> <Text id='poweroffice.postToPoGo'>Post to PowerOffice</Text></span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Previous Vouchers */}
                <div class='card'>
                    <div class='card-header'>
                        <strong><Text id='poweroffice.previousVouchers'>Previous Vouchers</Text></strong>
                    </div>
                    <div class='card-body'>
                        {historyLoading && (
                            <div class='text-center py-3'>
                                <i class='fa-solid fa-spinner fa-spin me-1' /> <Text id='poweroffice.loading'>Loading...</Text>
                            </div>
                        )}
                        {!historyLoading && history.length === 0 && (
                            <p class='text-body-secondary'><Text id='poweroffice.noVouchers'>No vouchers posted yet.</Text></p>
                        )}
                        {!historyLoading && history.length > 0 && (
                            <table class='table table-sm table-striped'>
                                <thead>
                                    <tr>
                                        <th><Text id='poweroffice.date'>Date</Text></th>
                                        <th><Text id='poweroffice.voucherNo'>Voucher No.</Text></th>
                                        <th><Text id='poweroffice.period'>Period</Text></th>
                                        <th><Text id='poweroffice.sales'>Sales</Text></th>
                                        <th class='text-end'><Text id='poweroffice.amount'>Amount</Text></th>
                                        <th><Text id='poweroffice.status'>Status</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(v => (
                                        <tr key={v.id}>
                                            <td>{v.voucherDate}</td>
                                            <td>{v.powerOfficeVoucherNumber || v.powerOfficeVoucherId || '-'}</td>
                                            <td>
                                                {v.periodType === 'daily' && <Text id='poweroffice.daily'>Daily</Text>}
                                                {v.periodType === 'weekly' && <Text id='poweroffice.weekly'>Weekly</Text>}
                                                {v.periodType !== 'daily' && v.periodType !== 'weekly' && <Text id='poweroffice.monthly'>Monthly</Text>}
                                            </td>
                                            <td>{(v.saleIds || []).length}</td>
                                            <td class='text-end'>{formatNumber(v.summary?.totalGross)}</td>
                                            <td>
                                                {v.status === 'posted' ? (
                                                    <span class='badge bg-success'><Text id='poweroffice.statusPosted'>Posted</Text></span>
                                                ) : (
                                                    <span class='badge bg-danger'><Text id='poweroffice.statusError'>Error</Text></span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default PowerofficeVoucherReporting;
